import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative bg-card border-r border-border overflow-hidden">
        <img
          src="https://images.pexels.com/photos/14314638/pexels-photo-14314638.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Freed Words"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="relative z-10 mt-auto p-12">
          <h2 className="font-heading text-4xl font-light tracking-tighter max-w-md">
            Recupera l'accesso al tuo <span className="text-brand-pink">account</span>.
          </h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-8">
            <img src="/logo.png" alt="Freed Words" className="h-8 w-auto" />
            <span className="font-heading text-lg font-semibold">Freed<span className="text-brand-pink">Words</span></span>
          </Link>

          {sent ? (
            <div data-testid="forgot-sent">
              <div className="h-12 w-12 rounded-2xl bg-brand-blue/10 flex items-center justify-center mb-5">
                <MailCheck className="h-6 w-6 text-brand-blue" />
              </div>
              <h1 className="font-heading text-3xl font-light tracking-tight mb-2">Controlla la tua email</h1>
              <p className="text-muted-foreground text-sm mb-8">
                Se l'indirizzo <strong>{email}</strong> è registrato, ti abbiamo inviato un link per reimpostare la password. Controlla anche lo spam. Il link è valido per 1 ora.
              </p>
              <Link to="/login" data-testid="back-to-login"
                className="inline-flex items-center gap-2 text-sm text-brand-blue hover:underline">
                <ArrowLeft className="h-4 w-4" /> Torna all'accesso
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-light tracking-tight mb-1">Password dimenticata?</h1>
              <p className="text-muted-foreground text-sm mb-8">Inserisci la tua email: ti invieremo un link per reimpostarla.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="forgot-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" />
                </div>
                {err && <p data-testid="forgot-error" className="text-sm text-destructive">{err}</p>}
                <Button type="submit" data-testid="forgot-submit" disabled={loading}
                  className="w-full rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white h-11">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia link di reset"}
                </Button>
              </form>
              <p className="text-sm text-muted-foreground text-center mt-6">
                <Link to="/login" data-testid="go-login" className="text-brand-blue hover:underline">Torna all'accesso</Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
